import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Select, {
  components,
  IndicatorsContainerProps,
  SingleValue,
} from 'react-select'
import Tippy from '@tippyjs/react'

import Icon from '../../ui/Icon'

interface SelectOption {
  value: string
  label: string
}

interface SelectOptionInputProps {
  id?: string
  name: string
  value: string
  list: SelectOption[]
  required?: boolean
  disabled?: boolean
  label?: string
  placeHolder?: string
  showPlaceholder?: boolean
  className?: string
  infoMessage?: string
  errorMessage?: string
  loading?: boolean
  isSearchable?: boolean
  onLoadMore?: () => void
  onSearch?: (value: string) => void
  onChange: (name: string, value: string) => void
}

export const SelectOptionInput = ({
  id,
  name,
  value,
  list,
  required = false,
  disabled = false,
  label,
  placeHolder = '',
  showPlaceholder,
  className,
  infoMessage,
  errorMessage,
  loading = false,
  isSearchable = true,
  onLoadMore,
  onSearch,
  onChange,
}: SelectOptionInputProps) => {
  const { t } = useTranslation('inputs')

  const [isError, setIsError] = useState(false)
  const [isTouch, setIsTouch] = useState(false)
  const [isFocus, setIsFocus] = useState(false)

  const selectedOption = list.find(item => item.value === value) ?? null
  const isRequired = isTouch && required && !value

  useEffect(() => {
    if (!required) {
      onChange(name, value)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleChange = useCallback(
    (option: SingleValue<SelectOption>) => {
      setIsTouch(true)

      const selectedValue = option?.value ?? ''

      setIsError(required && !selectedValue)
      onChange(name, selectedValue)
    },
    [name, onChange, required]
  )

  const IndicatorsContainer = useMemo(
    () => (props: IndicatorsContainerProps<SelectOption, false>) => (
      <components.IndicatorsContainer {...props}>
        {infoMessage && (
          <Tippy content={infoMessage} placement="top" className="ph-tooltip">
            <span
              className="input-icon-toggle ph-select-info"
              aria-label={infoMessage}
              onMouseDown={event => event.stopPropagation()}
              onClick={event => event.stopPropagation()}
            >
              <Icon name="info" size={16} />
            </span>
          </Tippy>
        )}

        {props.children}
      </components.IndicatorsContainer>
    ),
    [infoMessage]
  )

  const fieldClass = [
    'field',
    className,
    required && 'required',
    !isFocus && isRequired && 'is-required',
    !isFocus && isError && 'is-error',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={fieldClass}>
      {label && (
        <div className="label-row ph-select-label-row">
          <label htmlFor={id}>{label}</label>
        </div>
      )}
      <Select<SelectOption, false>
        inputId={id}
        value={selectedOption}
        options={list}
        onChange={handleChange}
        onFocus={() => setIsFocus(true)}
        onBlur={() => setIsFocus(false)}
        isDisabled={disabled}
        isLoading={loading}
        isSearchable={isSearchable}
        isClearable={!required}
        onMenuScrollToBottom={onLoadMore}
        onInputChange={(inputValue, actionMeta) => {
          if (actionMeta.action === 'input-change') {
            onSearch?.(inputValue)
          }
        }}
        filterOption={onSearch ? null : undefined}
        components={{
          IndicatorsContainer,
        }}
        unstyled
        classNames={{
          control: ({ isFocused }) =>
            `ph-select-control ${isFocused ? 'focused' : ''}`,
          valueContainer: () => 'ph-select-value',
          placeholder: () => 'ph-select-placeholder',
          singleValue: () => 'ph-select-single-value',
          input: () => 'ph-select-input',
          dropdownIndicator: () => 'ph-select-indicator',
          indicatorSeparator: () => 'ph-select-separator',
          menu: () => 'ph-select-menu',
          menuList: () => 'ph-select-menu-list',
          menuPortal: () => 'ph-select-menu-portal',
          option: ({ isFocused, isSelected }) =>
            `ph-select-option ${isFocused ? 'focused' : ''} ${
              isSelected ? 'selected' : ''
            }`,
        }}
        placeholder={
          showPlaceholder === false ? '' : placeHolder || t('select')
        }
        menuPortalTarget={document.body}
      />
      {errorMessage && !isFocus && (isRequired || isError) && (
        <div className="field-msg err" role="alert">
          <span className="ico">
            <Icon name="warn" size={13} />
          </span>
          {errorMessage}
        </div>
      )}
    </div>
  )
}
